import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'src/store';
import { DATE_PICKER } from './constant';

export const getLeaderboards = (state: RootState) =>
  state?.performanceDashboard?.leaderboards;

const getRankingsObj = (state: RootState) =>
  state?.performanceDashboard?.rankings;

export const getRankings = (state: RootState) => getRankingsObj(state)?.data;

export const getRankingColumns = (state: RootState) =>
  getRankingsObj(state)?.table?.columns || [];

export const getRankingsTableInfo = (state: RootState) =>
  getRankingsObj(state)?.table;

export const getIndividualRankings = (state: RootState) =>
  getRankingsObj(state)?.individual;

export const getRankingsMeta = (state: RootState) =>
  getRankingsObj(state)?.meta;

export const getRankingsListViewOptions = (state: RootState) =>
  getRankingsObj(state)?.listViewOptions;

const getKeyMetrics = (state: RootState) =>
  state?.performanceDashboard?.keyMetrics;

export const getKeyMetricsList = (state: RootState) =>
  getKeyMetrics(state)?.cards || [];

const getRawKeyMetricsFilters = (state: RootState) =>
  getKeyMetrics(state)?.filters || [];

function transformFilterConfig(filter: any): any {
  if (filter.type === 'date_granularity') {
    return {
      code: filter.code,
      type: 'date',
      subtype: filter.date_granularity_type,
      picker: DATE_PICKER[filter.date_granularity_type],
      hint: filter.hint,
      required: filter.required,
      min_date: -3155760000000,
      max_date: 3155760000000,
    };
  }
  if (filter.type === 'sifg' && filter.sifg_options?.inputs) {
    return {
      ...filter,
      returnCode: true,
      sifg_options: {
        ...filter.sifg_options,
        inputs: Object.fromEntries(
          Object.entries(
            (filter?.sifg_options?.inputs || {}) as Record<string, any[]>,
          ).map(([key, inputList]) => [
            key,
            inputList?.map(transformFilterConfig),
          ]),
        ),
      },
    };
  }
  return { ...filter, returnCode: true };
}

export const getKeyMetricsFilters = createSelector(
  getRawKeyMetricsFilters,
  (filters) => [...filters.map(transformFilterConfig)],
);

export const getKeyMetricsGroups = (state: RootState) =>
  getKeyMetrics(state)?.goal_groups?.data || [];

export const getKeyMetricsMetadata = (state: RootState) =>
  getKeyMetrics(state)?.metadata || [];

export const getStatus = (state: RootState, key) =>
  state?.performanceDashboard?.status?.[key];

const getKeyMetricsDetailsObj = (state: RootState) =>
  state?.performanceDashboard?.keyMetricsDetails;

export const getKeyMetricsDetails = (state: RootState) =>
  getKeyMetricsDetailsObj(state)?.data || {};

export const getKeyMetricsTeamBreakdown = (state: RootState) =>
  getKeyMetricsDetailsObj(state)?.teamBreakdown || {
    data: [],
    columns: [],
  };

export const getKeyMetricsBreakdownGraph = (state: RootState) =>
  getKeyMetricsDetailsObj(state)?.breakdownGraph;

export const getKeyMetricsTrendGraph = (state: RootState) =>
  getKeyMetricsDetailsObj(state)?.trendGraph;

export const getPerformanceSnapshot = (state: RootState) =>
  state?.performanceDashboard?.snapshot || [];
